/**
 * Разбор рынка рекуррентной серии на составляющие: актив, длительность окна и
 * само окно.
 *
 * Разбирается SLUG, а не заголовок. Заголовок («Bitcoin Up or Down - August 8,
 * 9:00PM-9:05PM ET») — английский текст с временем в чужой зоне, и любой его
 * парсер ломается от смены формулировки на стороне Polymarket. Slug же строго
 * машинный и содержит ровно то, что нужно:
 *
 *     btc-updown-5m-1786237200
 *     └┬┘ └──┬──┘ └┬┘ └───┬────┘
 *    актив   вид   окно   начало окна, unix-секунды
 *
 * Начало окна в секундах даёт и локальное время без возни с «ET», и ответ на
 * вопрос «это окно сейчас идёт?» — а именно за ним оператор и следит.
 */

/** Известные активы. Список закрыт намеренно: незнакомый префикс должен
 *  оставить рынок неразобранным, а не выдумать ему актив. */
const ASSETS: Record<string, string> = {
  btc: "BTC",
  eth: "ETH",
  sol: "SOL",
  xrp: "XRP",
  doge: "DOGE",
};

/** Длительности, которые встречаются в slug'ах серий. */
const DURATION_SECONDS: Record<string, number> = {
  "1m": 60,
  "5m": 300,
  "15m": 900,
  "1h": 3600,
  "4h": 14400,
  "1d": 86400,
};

export interface SeriesParts {
  /** Тикер актива в верхнем регистре: BTC, ETH. */
  asset: string;
  /** Длительность окна как в slug: 5m, 15m. */
  duration: string;
  /** Порядок длительности в секундах — для сортировки, а не для показа. */
  durationSec: number;
  /** Начало окна. */
  start: Date;
  /** Конец окна: начало плюс длительность. Polymarket его в slug не кладёт. */
  end: Date;
}

/**
 * Разбирает slug рынка серии. Возвращает null для всего остального — выборов,
 * макрособытий и любых рынков, чей slug не подходит под схему.
 */
export function parseSeriesSlug(slug: string): SeriesParts | null {
  // Схема: <asset>-updown-<duration>-<unix>. Именно `updown`: другие серии
  // Polymarket устроены иначе, и притворяться, что мы их понимаем, вредно.
  // Приставка `polyv2` — окна Protocol V2: окно и страйк у них те же, что у v1.
  const m = /^(?:polyv2)?([a-z]+)-updown-([0-9]+[mhd])-([0-9]{9,11})$/.exec(slug.trim().toLowerCase());
  if (!m) return null;

  const [, rawAsset, rawDuration, rawStart] = m;
  if (!rawAsset || !rawDuration || !rawStart) return null;

  const asset = ASSETS[rawAsset];
  const durationSec = DURATION_SECONDS[rawDuration];
  if (!asset || !durationSec) return null;

  const startSec = Number(rawStart);
  if (!Number.isFinite(startSec) || startSec <= 0) return null;

  const start = new Date(startSec * 1000);
  return {
    asset,
    duration: rawDuration,
    durationSec,
    start,
    end: new Date((startSec + durationSec) * 1000),
  };
}

/**
 * Минимум справочника, нужный навигации по серии.
 *
 * Имена полей — как в api/types.Market намеренно: так `Market[]` передаётся
 * сюда без перекладывания, а перекладывание — это место, где однажды перепутают
 * condition_id со slug.
 */
export interface SeriesCandidate {
  condition_id: string;
  slug: string;
  tracked: boolean;
}

export interface SeriesNeighbour {
  conditionId: string;
  parts: SeriesParts;
  /**
   * Сосед есть в справочнике, но трейдер на него не подписан: стакана по нему
   * не будет. Прятать такую ссылку нельзя — рынок существует, — но и молчать об
   * этом значит отправить оператора на пустой экран без объяснения.
   */
  tracked: boolean;
}

/**
 * Соседние окна ТОЙ ЖЕ серии: ближайшее меньшее и ближайшее большее начало.
 *
 * Ищется ближайшее начало, а НЕ `start ± durationSec`. В справочнике есть дыры:
 * на живом стеке шаги между сохранёнными окнами доходят до 2700 секунд при
 * пятиминутной серии, и арифметика промахнулась бы мимо существующего рынка,
 * объявив соседа отсутствующим.
 *
 * Серия — это совпадение актива И длительности: btc-updown-5m и btc-updown-15m
 * бывают с одним началом, и перепутать их значит увести оператора на рынок с
 * другим горизонтом.
 *
 * Времени функция не знает намеренно: «предыдущее» и «следующее» определяются
 * началом окна, а не фазой, иначе на прошедшем рынке «следующим» оказалось бы
 * что попало.
 */
export function findNeighbours(
  markets: readonly SeriesCandidate[],
  current: SeriesParts,
): { prev: SeriesNeighbour | null; next: SeriesNeighbour | null } {
  const t = current.start.getTime();
  let prev: SeriesNeighbour | null = null;
  let next: SeriesNeighbour | null = null;

  for (const m of markets) {
    const parts = parseSeriesSlug(m.slug);
    if (!parts) continue;
    if (parts.asset !== current.asset || parts.durationSec !== current.durationSec) continue;

    const start = parts.start.getTime();
    if (start === t) continue; // сам текущий рынок
    const candidate: SeriesNeighbour = {
      conditionId: m.condition_id,
      parts,
      tracked: m.tracked,
    };
    if (start < t) {
      if (prev === null || start > prev.parts.start.getTime()) prev = candidate;
    } else if (next === null || start < next.parts.start.getTime()) {
      next = candidate;
    }
  }
  return { prev, next };
}

/**
 * Идущее сейчас окно той же серии.
 *
 * Отдельно от `findNeighbours`, и это не удобство, а ответ на то, ради чего
 * навигация и заводилась. Окно закончилось — оператору нужен рынок, на котором
 * можно торговать ПРЯМО СЕЙЧАС, а не следующий по списку: на живом стеке между
 * завершившимся окном и идущим лежат несколько промежуточных, и все они
 * неотслеживаемые, то есть без стакана. Цепочка «след → след → след» провела бы
 * оператора по четырём пустым экранам.
 *
 * Возвращает null, если идущего окна в справочнике нет: серия могла прекратиться
 * либо справочник ещё не обновился. Выдумывать вместо этого ближайшее будущее
 * окно нельзя — на него нельзя поставить, оно ещё не открылось.
 */
export function findLiveWindow(
  markets: readonly SeriesCandidate[],
  current: SeriesParts,
  now: Date,
): SeriesNeighbour | null {
  for (const m of markets) {
    const parts = parseSeriesSlug(m.slug);
    if (!parts) continue;
    if (parts.asset !== current.asset || parts.durationSec !== current.durationSec) continue;
    if (parts.start.getTime() === current.start.getTime()) continue;
    if (windowPhase(parts, now) !== "live") continue;
    return { conditionId: m.condition_id, parts, tracked: m.tracked };
  }
  return null;
}

/** Сколько осталось до конца окна, мс. Ноль и меньше — окно закончилось. */
export function remainingInWindow(parts: SeriesParts, now: Date): number {
  return parts.end.getTime() - now.getTime();
}

/**
 * Остаток окна как «1:47».
 *
 * Минуты и секунды без часов: серии длиннее часа существуют, но остаток по ним
 * оператор и так читает по подписи окна, а «0:09» на пятиминутном рынке — это
 * то, на что он смотрит в последние секунды.
 */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const min = Math.floor(total / 60);
  const sec = total % 60;
  return `${min}:${String(sec).padStart(2, "0")}`;
}

export type WindowPhase = "live" | "upcoming" | "past";

/**
 * В какой фазе окно относительно момента `now`.
 *
 * Границы: начало включительно, конец исключительно — окно 9:00–9:05 в 9:05:00
 * уже прошлое, а не идущее.
 */
/**
 * Тон обратного отсчёта: сколько времени на решение ещё есть.
 *
 * Четыре ступени, и границы у них не круглые числа, а цена ошибки:
 *
 *   `soon` (30 с) — с этого момента итоговое окно усреднения уже НАЧАЛОСЬ
 *   (у TWAP-30 оно ровно тридцатисекундное), то есть каждая новая секунда
 *   попадает в итог и цена решения растёт;
 *
 *   `urgent` (4 с) — за `SettleDelay` до конца исход перестаёт зависеть от
 *   рынка вовсе; четыре секунды это две на запертость плюс две на путь заявки
 *   до стакана. Позже уже поздно, и цвет обязан сказать это раньше цифр;
 *
 *   `over` — окно кончилось, торговать нечем.
 *
 * Отдельная функция, а не тернарник в разметке: границы здесь — торговое
 * правило, а правило проверяется тестом.
 */
export type CountdownTone = "live" | "soon" | "urgent" | "over";

export const COUNTDOWN_SOON_MS = 30_000;
export const COUNTDOWN_URGENT_MS = 4_000;

export function countdownTone(remainingMs: number): CountdownTone {
  if (remainingMs <= 0) return "over";
  if (remainingMs < COUNTDOWN_URGENT_MS) return "urgent";
  if (remainingMs < COUNTDOWN_SOON_MS) return "soon";
  return "live";
}

export function windowPhase(parts: SeriesParts, now: Date): WindowPhase {
  const t = now.getTime();
  if (t < parts.start.getTime()) return "upcoming";
  if (t < parts.end.getTime()) return "live";
  return "past";
}

/**
 * Окно в локальном времени: «21:00–21:05», с датой, если окно не сегодняшнее.
 *
 * Локальное, а не ET: оператор сверяет его с часами у себя, и перевод в голове —
 * лишний способ ошибиться на пятиминутном рынке.
 */
export function formatWindow(parts: SeriesParts, now: Date): string {
  const hhmm = (d: Date) =>
    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const span = `${hhmm(parts.start)}–${hhmm(parts.end)}`;
  const sameDay =
    parts.start.getFullYear() === now.getFullYear() &&
    parts.start.getMonth() === now.getMonth() &&
    parts.start.getDate() === now.getDate();
  if (sameDay) return span;
  const date = `${String(parts.start.getDate()).padStart(2, "0")}.${String(
    parts.start.getMonth() + 1,
  ).padStart(2, "0")}`;
  return `${date} ${span}`;
}
